import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useEffect, useState } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  CreditCard,
  Plus,
  Edit,
  Trash2,
  Users,
  FileText,
  Layers,
  Package,
  Building2,
  Check,
  X,
} from 'lucide-react';
import { useAdminStore } from '../store/admin-store';
import { esSinLimite, formatearLimite } from '@/lib/utils/limites';
import type { GrupoDeModulos, Plan } from '../types/admin.types';
import { plansService } from '../services/plans-service';
import { useToast } from '@/lib/hooks/use-toast';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';

const TIER_COLORS: Record<string, string> = {
  TRIAL: 'border-slate-500/30 bg-slate-500/5',
  BASIC: 'border-blue-500/30 bg-blue-500/5',
  PROFESSIONAL: 'border-purple-500/30 bg-purple-500/5',
  ENTERPRISE: 'border-amber-500/30 bg-amber-500/5',
};

const TIER_BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  TRIAL: 'secondary',
  BASIC: 'outline',
  PROFESSIONAL: 'default',
  ENTERPRISE: 'destructive',
};

const FEATURE_LABELS: Record<string, string> = {
  aiAssistant: 'Asistente IA',
  dynamicPricing: 'Precios Dinamicos',
  sapIntegration: 'Integración SAP',
  importTracking: 'Seguimiento Importaciones',
  ecommerce: 'E-commerce',
};

const EMPTY_PLAN: Partial<Plan> = {
  name: '',
  tier: undefined as any,
  description: '',
  monthlyPrice: 0,
  annualPrice: 0,
  maxUsers: 5,
  maxQuotesPerMonth: 100,
  maxPricingModels: 3,
  maxOrganizations: 1,
  maxMaterials: 500,
  trialDays: 0,
  sortOrder: 0,
  features: {
    aiAssistant: true,
    dynamicPricing: false,
    sapIntegration: false,
    importTracking: false,
    ecommerce: false,
  },
};

export function AdminPlansPage() {
  // Las acciones que cortan accesos preguntan antes. Ver confirm-dialog.tsx.
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const { toast } = useToast();
  const { plans, fetchPlans, createPlan, updatePlan, deletePlan } = useAdminStore();
  const [dialog, setDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Partial<Plan> | null>(null);
  const [form, setForm] = useState<Partial<Plan>>(EMPTY_PLAN);
  const [catalogoDeModulos, setCatalogoDeModulos] = useState<GrupoDeModulos[]>([]);
  const [catalogoFallo, setCatalogoFallo] = useState(false);

  useEffect(() => {
    fetchPlans();
    plansService
      .getCatalogoDeModulos()
      .then(setCatalogoDeModulos)
      .catch(() => setCatalogoFallo(true));
  }, [fetchPlans]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(EMPTY_PLAN);
    setDialog(true);
  };

  const openEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      description: plan.description,
      monthlyPrice: plan.monthlyPrice,
      annualPrice: plan.annualPrice,
      maxUsers: plan.maxUsers,
      maxQuotesPerMonth: plan.maxQuotesPerMonth,
      maxPricingModels: plan.maxPricingModels,
      maxOrganizations: plan.maxOrganizations,
      maxMaterials: plan.maxMaterials,
      trialDays: plan.trialDays,
      sortOrder: plan.sortOrder,
      modules: plan.modules ?? [],
      features: plan.features || {},
    });
    setDialog(true);
  };

  // Sin módulos, la prueba deja a la plataforma sin altas: no se deja guardar así.
  const pruebaSinModulos = editingPlan?.tier === 'TRIAL' && (form.modules ?? []).length === 0;

  const handleSave = async () => {
    if (pruebaSinModulos) return;
    try {
      if (editingPlan?.id) {
        await updatePlan(editingPlan.id, form);
      } else {
        await createPlan(form);
      }
      setDialog(false);
    } catch (err: any) {
      toast({
        title: 'No se pudo guardar el plan',
        description: err?.response?.data?.error ?? err?.message ?? 'Intentá de nuevo.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = (id: string, nombre: string) => {
    /*
      Era el único lugar del backoffice que confirmaba algo, y lo hacía con
      `window.confirm`: no se puede estilar ni traducir, no atrapa el foco, y en
      algunos navegadores se puede silenciar. Se usa el diálogo de la aplicación,
      igual que en el resto.
    */
    confirm({
      title: `¿Desactivar el plan ${nombre}?`,
      description:
        'Deja de estar disponible para asignar. Las empresas que ya lo tienen no se ven afectadas.',
      confirmText: 'Sí, desactivar',
      variant: 'destructive',
      onConfirm: () => deletePlan(id),
    });
  };

  const alternarModulo = (key: string) => {
    const actuales = new Set(form.modules ?? []);
    if (actuales.has(key)) actuales.delete(key);
    else actuales.add(key);
    setForm({ ...form, modules: [...actuales] });
  };

  const toggleFeature = (key: string) => {
    setForm({
      ...form,
      features: { ...((form.features as any) || {}), [key]: !(form.features as any)?.[key] },
    });
  };

  return (
    <AppLayout variante="plataforma">
      <ConfirmDialog />
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6" />
            <div className="mb-3">
              <EtiquetaDePagina tono="neutro">Plataforma</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>Planes</h1>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" /> Nuevo Plan
          </Button>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => (
            <Card key={plan.id} className={`relative ${TIER_COLORS[plan.tier] || ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Badge variant={TIER_BADGE_VARIANTS[plan.tier] || 'outline'}>{plan.tier}</Badge>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(plan)}
                      aria-label={`Editar el plan ${plan.name}`}
                    >
                      <Edit className="h-3 w-3" aria-hidden="true" />
                    </Button>
                    {(plan.tier as string) !== 'DEMO' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(plan.id, plan.name)}
                        aria-label={`Desactivar el plan ${plan.name}`}
                      >
                        <Trash2 className="h-3 w-3 text-red-400" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Price */}
                <div>
                  <span className="text-3xl font-bold">${plan.monthlyPrice}</span>
                  <span className="text-muted-foreground">/mes</span>
                  {plan.annualPrice ? (
                    <div className="text-xs text-muted-foreground">${plan.annualPrice}/anio</div>
                  ) : null}
                </div>

                {/* Limits */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{formatearLimite(plan.maxUsers, 'usuarios', 'Usuarios ilimitados')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {esSinLimite(plan.maxQuotesPerMonth)
                        ? 'Cotizaciones ilimitadas'
                        : `${plan.maxQuotesPerMonth.toLocaleString('es-BO')} cotizaciones/mes`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {formatearLimite(plan.maxPricingModels, 'modelos', 'Modelos ilimitados')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {formatearLimite(plan.maxMaterials, 'materiales', 'Materiales ilimitados')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {formatearLimite(
                        plan.maxOrganizations,
                        'organizaciones',
                        'Organizaciones ilimitadas'
                      )}
                    </span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-1 pt-2 border-t border-border/50">
                  {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                    const enabled = (plan.features as any)?.[key];
                    return (
                      <div key={key} className="flex items-center gap-2 text-sm">
                        {enabled ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <X className="h-3 w-3 text-muted-foreground/50" />
                        )}
                        <span className={enabled ? '' : 'text-muted-foreground/50'}>{label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Subscriber count */}
                {plan.subscriberCount !== undefined && (
                  <div className="text-xs text-muted-foreground pt-2 border-t border-border/50">
                    {plan.subscriberCount} suscriptores
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialog} onOpenChange={setDialog}>
          <DialogContent
            aria-describedby={undefined}
            className="max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <DialogHeader>
              <DialogTitle>{editingPlan ? 'Editar Plan' : 'Nuevo Plan'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Nombre</label>
                <Input
                  value={form.name || ''}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              {!editingPlan && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Tier</label>
                  <Select
                    value={form.tier}
                    onValueChange={(v: any) => setForm({ ...form, tier: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRIAL">Trial</SelectItem>
                      <SelectItem value="BASIC">Basic</SelectItem>
                      <SelectItem value="PROFESSIONAL">Professional</SelectItem>
                      <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-1 block">Descripción</label>
                <Input
                  value={form.description || ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Precio mensual ($)</label>
                  <Input
                    type="number"
                    value={form.monthlyPrice || 0}
                    onChange={(e) => setForm({ ...form, monthlyPrice: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Precio anual ($)</label>
                  <Input
                    type="number"
                    value={form.annualPrice || 0}
                    onChange={(e) => setForm({ ...form, annualPrice: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="text-sm font-medium">Limites</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Max Usuarios</label>
                  <Input
                    type="number"
                    value={form.maxUsers || 0}
                    onChange={(e) => setForm({ ...form, maxUsers: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    Max Cotizaciones/mes
                  </label>
                  <Input
                    type="number"
                    value={form.maxQuotesPerMonth || 0}
                    onChange={(e) =>
                      setForm({ ...form, maxQuotesPerMonth: Number(e.target.value) })
                    }
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Max Modelos</label>
                  <Input
                    type="number"
                    value={form.maxPricingModels || 0}
                    onChange={(e) => setForm({ ...form, maxPricingModels: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Max Materiales</label>
                  <Input
                    type="number"
                    value={form.maxMaterials || 0}
                    onChange={(e) => setForm({ ...form, maxMaterials: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    Max Organizaciones
                  </label>
                  <Input
                    type="number"
                    value={form.maxOrganizations || 0}
                    onChange={(e) => setForm({ ...form, maxOrganizations: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Días de prueba</label>
                  <Input
                    type="number"
                    value={form.trialDays || 0}
                    onChange={(e) => setForm({ ...form, trialDays: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="text-sm font-medium">Funcionalidades</div>
              <div className="space-y-2">
                {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(form.features as any)?.[key] || false}
                      onChange={() => toggleFeature(key)}
                      className="rounded"
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>

              {catalogoFallo && (
                <p className="text-xs text-destructive">
                  No se pudo cargar el catálogo de módulos. Cerrá y volvé a abrir el plan.
                </p>
              )}
              {catalogoDeModulos.length > 0 && (
                <>
                  <div className="text-sm font-medium">Módulos incluidos</div>
                  <p className="text-xs text-muted-foreground">
                    {editingPlan?.tier === 'TRIAL'
                      ? 'Es lo que ve una empresa nueva durante su prueba. Sin ningún módulo no se pueden dar de alta empresas nuevas. Editarlo no cambia a las empresas que ya existen.'
                      : 'Con ninguno marcado, el plan no limita los módulos. Editarlo no cambia a las empresas que ya lo tienen.'}
                  </p>
                  <div className="space-y-3">
                    {catalogoDeModulos.map((grupo) => (
                      <div key={grupo.titulo} className="space-y-1">
                        <div className="text-xs text-muted-foreground">{grupo.titulo}</div>
                        {grupo.items.map((item) => {
                          // Si otro módulo marcado lo arrastra, va incluido igual: desmarcarlo no haría nada.
                          const arrastradoPor = (item.impliedBy ?? []).filter((o) =>
                            (form.modules ?? []).includes(o.key)
                          );
                          const incluidoPor = arrastradoPor.length > 0;
                          return (
                            <label
                              key={item.key}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={incluidoPor || (form.modules ?? []).includes(item.key)}
                                disabled={incluidoPor}
                                onChange={() => alternarModulo(item.key)}
                                className="rounded"
                              />
                              <span className="text-sm">{item.label}</span>
                              {incluidoPor && (
                                <span className="text-xs text-muted-foreground">
                                  incluido por {arrastradoPor.map((o) => o.label).join(', ')}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {pruebaSinModulos && (
                <p className="text-xs text-destructive">
                  La prueba tiene que incluir al menos un módulo: sin módulos no se pueden dar de
                  alta empresas nuevas.
                </p>
              )}
              <Button
                className="w-full"
                onClick={handleSave}
                disabled={!form.name || pruebaSinModulos}
              >
                {editingPlan ? 'Guardar Cambios' : 'Crear Plan'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
